import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query'
import { eventTimelineService, venueTimelineService } from '@/services/TimelineService'
import { timelineKeys } from '@/hooks/queryKeys'
import type {
  EventTimelineDto,
  VenueTimelineTemplateDto,
  VenueTimelineTemplateListDto,
} from '@lib/dto/event-timeline.dto'
import type {
  ApplyVenueTimelineTemplateInput,
  CreateEventTimelineEntryInput,
  CreateVenueTimelineTemplateInput,
  UpdateEventTimelineEntryInput,
  UpdateVenueTimelineTemplateInput,
} from '@lib/input/event-timeline.input'

/**
 * A venue's timeline templates.
 *
 * `enabled` because the route is admin-only and a client's event page renders
 * the same timeline component: asking anyway answered 403 twice per view and
 * logged it as a failure, which is a real error in the console for a thing that
 * was never going to work.
 */
export function useVenueTimelineTemplates(
  venueId: string,
  enabled = true,
): UseQueryResult<VenueTimelineTemplateListDto> {
  return useQuery({
    queryKey: timelineKeys.templates(venueId),
    queryFn: () => venueTimelineService.list(venueId),
    staleTime: 60_000,
    enabled,
  })
}

export function useCreateTimelineTemplate(
  venueId: string,
): UseMutationResult<VenueTimelineTemplateDto, Error, CreateVenueTimelineTemplateInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateVenueTimelineTemplateInput) => venueTimelineService.create(venueId, input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: timelineKeys.templates(venueId) }),
  })
}

export function useUpdateTimelineTemplate(
  venueId: string,
): UseMutationResult<VenueTimelineTemplateDto, Error, { templateId: string; input: UpdateVenueTimelineTemplateInput }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { templateId: string; input: UpdateVenueTimelineTemplateInput }) =>
      venueTimelineService.update(venueId, vars.templateId, vars.input),
    // Invalidated rather than written in: making one template the default clears
    // the flag on the others, so the row that came back is not the only one that
    // changed.
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: timelineKeys.templates(venueId) }),
  })
}

export function useDeleteTimelineTemplate(
  venueId: string,
): UseMutationResult<VenueTimelineTemplateListDto, Error, { templateId: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { templateId: string }) => venueTimelineService.remove(venueId, vars.templateId),
    onSuccess: (data) => queryClient.setQueryData(timelineKeys.templates(venueId), data),
  })
}

/** An event's run of the day. */
export function useEventTimeline(eventId: string): UseQueryResult<EventTimelineDto> {
  return useQuery({
    queryKey: timelineKeys.forEvent(eventId),
    queryFn: () => eventTimelineService.get(eventId),
    staleTime: 30_000,
  })
}

/**
 * Every write answers with the whole timeline, so each of these replaces the
 * cache outright. A run sheet is small and read together; patching one row into
 * a list that the server may have re-ordered is more ways to be wrong than it is
 * worth.
 */
export function useAddTimelineEntry(
  eventId: string,
): UseMutationResult<EventTimelineDto, Error, CreateEventTimelineEntryInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateEventTimelineEntryInput) => eventTimelineService.add(eventId, input),
    onSuccess: (data) => queryClient.setQueryData(timelineKeys.forEvent(eventId), data),
  })
}

export function useUpdateTimelineEntry(
  eventId: string,
): UseMutationResult<EventTimelineDto, Error, { entryId: string; input: UpdateEventTimelineEntryInput }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { entryId: string; input: UpdateEventTimelineEntryInput }) =>
      eventTimelineService.update(eventId, vars.entryId, vars.input),
    onSuccess: (data) => queryClient.setQueryData(timelineKeys.forEvent(eventId), data),
  })
}

export function useRemoveTimelineEntry(
  eventId: string,
): UseMutationResult<EventTimelineDto, Error, { entryId: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { entryId: string }) => eventTimelineService.remove(eventId, vars.entryId),
    onSuccess: (data) => queryClient.setQueryData(timelineKeys.forEvent(eventId), data),
  })
}

export function useApplyTimelineTemplate(
  eventId: string,
): UseMutationResult<EventTimelineDto, Error, ApplyVenueTimelineTemplateInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: ApplyVenueTimelineTemplateInput) => eventTimelineService.apply(eventId, input),
    onSuccess: (data) => queryClient.setQueryData(timelineKeys.forEvent(eventId), data),
  })
}
